/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef, type JSX } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentPipeline.module.scss';

type Translate = (key: string) => string;

type Step = {
  no: string;
  title: string;
  body: string;
  icon: JSX.Element;
};

const getSteps = (t: Translate): Step[] => [
  {
    no: '01',
    title: t('Licensed acquisition'),
    body: t(
      'Taken from the government register that issued the record, under the access arrangement that register requires — including registers that charge for or restrict access.',
    ),
    icon: (
      <>
        <rect x="4" y="9.5" width="16" height="10.5" rx="2" />
        <path d="M8 9.5V7a4 4 0 0 1 8 0v2.5" />
      </>
    ),
  },
  {
    no: '02',
    title: t('Structure and normalise'),
    body: t(
      'Native formats, languages and scripts are parsed into a common schema. The original value is retained alongside the normalised one.',
    ),
    icon: (
      <>
        <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
        <path d="M3.5 10h17M9.5 10v9.5" />
      </>
    ),
  },
  {
    no: '03',
    title: t('Resolve the entity'),
    body: t(
      'Matched on registration identifiers first, then name, address, officer and ownership signals. Below threshold, candidates are surfaced rather than merged.',
    ),
    icon: (
      <>
        <circle cx="11" cy="11" r="7.5" />
        <path d="M11 6.5V11l3 2" strokeLinecap="round" />
        <path d="M20 20l-2.4-2.4" strokeLinecap="round" />
      </>
    ),
  },
  {
    no: '04',
    title: t('Attach provenance'),
    body: t(
      'Register, document class and retrieval timestamp are bound to the field, not to the record. Modelled values are labelled at this step and never later.',
    ),
    icon: (
      <>
        <path d="M12.5 3.5 20 11l-8.2 8.2a2.3 2.3 0 0 1-3.25 0L4 14.65a2.3 2.3 0 0 1 0-3.25z" />
        <circle cx="15" cy="8" r="1.4" fill="currentColor" stroke="none" />
      </>
    ),
  },
  {
    no: '05',
    title: t('Deliver and retain'),
    body: t(
      'Served through API, platform, bulk or Regis. Every prior state is kept, which is what makes a point-in-time reconstruction possible.',
    ),
    icon: (
      <>
        <path d="M5 12h13M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 19h13" strokeLinecap="round" />
      </>
    ),
  },
];

export const GovernmentPipeline = () => {
  const { t } = useInt();
  const spineRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(spineRef, { threshold: 0.1 });
  const steps = getSteps(t);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('How a record gets from a register to your case file.')}
          description={t(
            'Five steps, and the register is named at every one of them. If a value cannot be traced back to step one, it does not ship as filed.',
          )}
          align="left"
        />
      </div>

      <div className={cn(styles.spine, revealed && styles.spineOn)} ref={spineRef}>
        {steps.map((step, i) => (
          <div className={styles.step} style={{ transitionDelay: `${i * 90}ms` }} key={step.no}>
            <span className={styles.stepIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {step.icon}
              </svg>
            </span>
            <div className={styles.stepBody}>
              <span className={styles.stepNo}>{step.no}</span>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepText}>{step.body}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
